import React from 'react';
import { Recommendation } from '../types';
import { SparklesIcon, LightbulbIcon, GamepadIcon, PlayIcon, BriefcaseIcon } from 'shared-icons';

interface RecommendationsProps {
  recommendations: {
    general: Recommendation[];
    gaming: Recommendation[];
    streaming: Recommendation[];
    remoteWork: Recommendation[];
  } | null;
  isLoading: boolean;
  error: string | null;
}

const Category: React.FC<{ title: string; icon: React.ReactNode; recommendations: Recommendation[] }> = ({ title, icon, recommendations }) => {
  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  return (
    <div className="bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm p-4 rounded-lg border border-white/30 dark:border-slate-700/50">
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400 rounded-lg flex items-center justify-center">
          {icon}
        </div>
        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">{title}</h3>
      </div>
      <ul className="space-y-2">
        {recommendations.map((rec, index) => (
          <li key={index}>
            <p className="font-semibold text-slate-700 dark:text-slate-300">{rec.title}</p>
            <p className="text-sm text-gray-600 dark:text-slate-400">{rec.description}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Recommendations: React.FC<RecommendationsProps> = ({ recommendations, isLoading, error }) => {
  const categories = recommendations ? [
    { title: 'General Tips', icon: <LightbulbIcon />, data: recommendations.general },
    { title: 'For Gamers', icon: <GamepadIcon />, data: recommendations.gaming },
    { title: 'For Streamers', icon: <PlayIcon />, data: recommendations.streaming },
    { title: 'For Remote Work', icon: <BriefcaseIcon />, data: recommendations.remoteWork },
  ] : [];

  return (
    <div className="w-full max-w-4xl mt-8">
      <div className="flex items-center gap-3 mb-4">
        <SparklesIcon className="w-6 h-6 text-purple-500 dark:text-purple-400" />
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">AI-Powered Recommendations</h2>
      </div>

      {isLoading && (
        <div className="text-center p-8 bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm rounded-lg border border-white/30 dark:border-slate-700/50">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto mb-3"></div>
          <p className="text-slate-600 dark:text-slate-400">Generating personalized recommendations...</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg border border-red-200 dark:border-red-800">
          <p><strong>Error:</strong> {error}</p>
        </div>
      )}

      {!isLoading && !error && recommendations && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categories.map(cat => (
            <Category key={cat.title} title={cat.title} icon={cat.icon} recommendations={cat.data} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Recommendations;